import * as THREE from 'three';
import { ConvexHull } from 'three/examples/jsm/math/ConvexHull.js';

import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';

/** Hull vertices preserve every perspective support plane without adding empty box corners. */
export function reduceCameraEnvelope(points: readonly Vector3Tuple[]): Vector3Tuple[] {
    const unique = [...new Map(points.map((point) => [JSON.stringify(point), point])).values()];
    if (unique.length < 4) return unique;
    const origin = new THREE.Vector3(...unique[0]!);
    const local = unique.map((point) => new THREE.Vector3(...point).sub(origin));
    const farthest = local.reduce((a, b) => a.lengthSq() > b.lengthSq() ? a : b);
    const normal = local.map((point) => new THREE.Vector3().crossVectors(farthest, point))
        .reduce((a, b) => a.lengthSq() > b.lengthSq() ? a : b);
    const scale = farthest.length();
    const unitNormal = normal.clone().normalize();
    // Three's 3D hull requires a nondegenerate tetrahedron; lower-dimensional envelopes remain valid.
    if (normal.length() <= scale * scale * 1e-12
        || local.every((point) => Math.abs(point.dot(unitNormal)) <= scale * 1e-12)) return unique;
    const hull = new ConvexHull().setFromPoints(local);
    const vertices = new Set<THREE.Vector3>();
    for (const face of hull.faces) {
        let edge = face.edge;
        do {
            vertices.add(edge.head().point);
            edge = edge.next;
        } while (edge !== face.edge);
    }
    // Retain points discarded within QuickHull's tolerance if they lie outside its computed faces.
    for (const point of local) {
        if (hull.faces.some((face) => face.normal.dot(point) > face.constant)) vertices.add(point);
    }
    return [...vertices].map((point) => point.clone().add(origin).toArray());
}
